/**
 * LaborCategoryCatalog — Settings ▸ Labor Categories. Resource Plan Epic 11 (RP-11.1).
 *
 * TERMINOLOGY (§3): Labor Category, never "Role". The tooltip on the page header
 * translates for [FIN], who know this list as the rate card's "Role" column.
 *
 * Mix % and Avg Internal Cost are shown here but NOT edited here — they are Rate Card
 * fields (RP-12.1), and the mix carries a cross-row invariant that a per-row edit cannot
 * satisfy. The link sends the admin to the card.
 */

import { Link } from "@tanstack/react-router";
import { Info, MoreHorizontal, Pencil, Tags } from "lucide-react";
import { useState } from "react";
import { LaborCategoryEditorDialog } from "@/components/admin/LaborCategoryEditorDialog";
import { SolutionEstimateRoleMap } from "@/components/admin/SolutionEstimateRoleMap";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDisciplines } from "@/hooks/use-disciplines";
import {
  type LaborCategoryView,
  useLaborCategories,
  useUpdateLaborCategory,
} from "@/hooks/use-rate-card";

type DialogState =
  | { type: "none" }
  | { type: "create" }
  | { type: "edit"; category: LaborCategoryView };

export function LaborCategoryCatalog() {
  const { data: categories = [], isLoading, isError, error, refetch } = useLaborCategories();
  const { data: disciplines = [] } = useDisciplines();
  const update = useUpdateLaborCategory();
  const [dialog, setDialog] = useState<DialogState>({ type: "none" });

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load the Labor Category Catalog"}
        onRetry={refetch}
      />
    );
  }

  const showCost = categories.some((c) => c.avgInternalCost !== undefined);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 font-display text-lg font-semibold">
            Labor Category Catalog
            <span
              title="The Nymbl Standard Rate Card heads this list “Role”. In Nymbl Staff, Role means a Pod Position (R1–R4, Supporting) or a User Role, so the priced commercial category is a Labor Category."
              className="cursor-help text-muted-foreground"
            >
              <Info className="h-4 w-4" />
            </span>
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            The canonical list every Plan Line prices against. A category is Pod-eligible only when
            it maps to a Discipline — the rest plan and cost without one.
          </p>
        </div>
        <Button size="sm" onClick={() => setDialog({ type: "create" })}>
          Add Labor Category
        </Button>
      </header>

      <DataTable
        headers={[
          { label: "Labor Category" },
          { label: "Discipline" },
          { label: "Expected rating", className: "hidden md:table-cell" },
          { label: "Mix %", className: "text-right" },
          ...(showCost ? [{ label: "Avg Internal Cost", className: "text-right" }] : []),
          { label: "Rates", className: "text-right" },
          { label: "Status" },
          { label: "Actions", className: "text-right" },
        ]}
        loading={isLoading}
        empty={
          <EmptyState
            icon={Tags}
            title="No Labor Categories yet"
            description="Seed the Nymbl Standard Rate Card, or add the first category."
          />
        }
      >
        {categories.map((category) => (
          <tr
            key={category.id}
            className="border-b border-border last:border-0 transition-colors hover:bg-muted/30"
          >
            <td className="px-4 py-3">
              <div className="font-medium">{category.name}</div>
              {category.description && (
                <div className="text-xs text-muted-foreground">{category.description}</div>
              )}
            </td>
            <td className="px-4 py-3">
              {category.podEligible ? (
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{category.disciplineCode}</Badge>
                  {category.isDefaultForDiscipline && (
                    <span
                      className="text-xs text-muted-foreground"
                      title="The default Labor Category for this Discipline — used when a Pod Shape Position needs one."
                    >
                      default
                    </span>
                  )}
                </div>
              ) : (
                <span
                  className="text-xs text-muted-foreground"
                  title="Plan-and-cost only. It prices and costs normally, and arrives at Handover as Supporting or unmapped demand."
                >
                  Not Pod-eligible
                </span>
              )}
            </td>
            <td className="hidden px-4 py-3 text-xs text-muted-foreground md:table-cell">
              {category.expectedRatingMin === null
                ? "—"
                : `${category.expectedRatingMin}–${category.expectedRatingMax}`}
            </td>
            <td className="px-4 py-3 text-right tabular-nums">{category.projectMixPercent}%</td>
            {showCost && (
              <td className="px-4 py-3 text-right tabular-nums">
                {category.avgInternalCost === undefined ? "—" : `$${category.avgInternalCost}`}
              </td>
            )}
            <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">
              {category.rateCount}
            </td>
            <td className="px-4 py-3">
              {category.active ? (
                <Badge variant="secondary">Active</Badge>
              ) : (
                <Badge variant="outline">Inactive</Badge>
              )}
            </td>
            <td className="px-4 py-3 text-right">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setDialog({ type: "edit", category })}>
                    <Pencil className="h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => update.mutate({ id: category.id, active: !category.active })}
                  >
                    {category.active ? "Deactivate" : "Reactivate"}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </td>
          </tr>
        ))}
      </DataTable>

      <p className="text-xs text-muted-foreground">
        Mix % and Avg Internal Cost are set on the{" "}
        <Link to="/dashboard/settings/rate-card" className="underline underline-offset-2">
          Rate Card
        </Link>
        , where the mix is checked as a whole — active categories must total exactly 100.00%.
        Categories are deactivated, never deleted, so priced history is never orphaned.
      </p>

      {(dialog.type === "create" || dialog.type === "edit") && (
        <LaborCategoryEditorDialog
          open
          onOpenChange={(open) => !open && setDialog({ type: "none" })}
          disciplines={disciplines}
          category={dialog.type === "edit" ? dialog.category : undefined}
        />
      )}

      <SolutionEstimateRoleMap />
    </div>
  );
}
